// Records first / last / last-Meta-paid touches in the w369_attr cookie (spec §4.2).
// Written only with granted ad consent; in consent regions the landing touch waits
// in sessionStorage until the visitor accepts, and is expired/discarded if they
// decline. The decision logic (planLanding) and cookie diffing (nextAttributionCookie)
// are pure functions tested in tests/tracking/attributionCookie.test.ts — this
// plugin only wires DOM (cookies, sessionStorage) to them and applies what they
// return.
import {
  ATTRIBUTION_COOKIE,
  ATTRIBUTION_MAX_AGE_SEC,
  buildTouch,
  nextAttributionCookie,
  parsePendingTouch,
  planLanding,
  type LandingPlan,
  type PendingTouch,
} from '~/utils/tracking/attributionCookie';
import { buildCookie, readCookie } from '~/utils/tracking/consentRecord';
import { isEmbedPath } from '~/data/tool-embeds.mjs';

const PENDING_KEY = 'w369_attr_pending';

// In-memory fallback for when sessionStorage throws (blocked/private browsing): an
// in-page consent Accept can still flush the pending touch within the same load.
let memoryPending: string | null = null;

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return;
  // Embedded calculators sit in other sites' iframes: no consent record, no touch.
  if (isEmbedPath(window.location.pathname)) return;

  const { adConsent, load } = useCookieConsent();
  load();

  const landingUrl = new URL(window.location.href);
  const landing: PendingTouch = {
    href: landingUrl.href,
    touch: buildTouch(landingUrl, document.referrer, Date.now()),
  };

  const secure = () => window.location.protocol === 'https:';

  const writeTouch = (touch: PendingTouch['touch'], url: URL) => {
    const next = nextAttributionCookie(readCookie(document.cookie, ATTRIBUTION_COOKIE), touch, url);
    if (next === null) return;
    document.cookie = buildCookie(ATTRIBUTION_COOKIE, next, ATTRIBUTION_MAX_AGE_SEC, window.location.hostname, secure());
  };

  const expireCookie = () => {
    if (readCookie(document.cookie, ATTRIBUTION_COOKIE) === undefined) return;
    document.cookie = buildCookie(ATTRIBUTION_COOKIE, '', 0, window.location.hostname, secure());
  };

  const readPending = (): string | null => {
    try {
      return window.sessionStorage.getItem(PENDING_KEY);
    } catch {
      return memoryPending;
    }
  };

  const writePending = (touch: PendingTouch) => {
    const raw = JSON.stringify(touch);
    memoryPending = raw;
    try {
      window.sessionStorage.setItem(PENDING_KEY, raw);
    } catch {
      // storage blocked — the in-memory fallback still covers an in-page Accept
    }
  };

  const clearPending = () => {
    memoryPending = null;
    try {
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch {
      // storage blocked
    }
  };

  const applyLandingPlan = (plan: LandingPlan, landingTouch: PendingTouch) => {
    for (const entry of plan.persist) writeTouch(entry.touch, new URL(entry.href));
    if (plan.pending === 'write') writePending(landingTouch);
    else if (plan.pending === 'clear') clearPending();
    if (plan.expireCookie) expireCookie();
  };

  // At load: decide from the current consent, the stored pending touch (if any)
  // and this landing, then apply.
  applyLandingPlan(planLanding(adConsent.value, readPending(), landing), landing);

  // React to every later consent change, not just an initially-unknown one — a
  // visitor can revoke (or grant) marketing consent from the preferences dialog
  // well after the page load's own decision, and that must still reach the cookie.
  const flushPending = () => {
    const pendingRaw = readPending();
    // Target the pending touch itself, not this load's landing: the pending touch
    // is the actual paid/organic visit that was waiting on a decision, and
    // persisting the reloaded landing instead would replace last/last_meta with
    // the reload rather than the touch that was actually waiting.
    const target = parsePendingTouch(pendingRaw) ?? landing;
    applyLandingPlan(planLanding('granted', pendingRaw, target), target);
  };

  const applyDenied = () => applyLandingPlan(planLanding('denied', readPending(), landing), landing);

  watch(adConsent, (value) => {
    if (value === 'granted') flushPending();
    else if (value === 'denied') applyDenied();
  });
});
